import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminFetch, getAdminToken, setAdminToken } from "../../api/adminApi";
import "./admin.css";

function navClass({ isActive }) {
  return [
    "rounded-full px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.18em] transition-all duration-200",
    isActive
      ? "border border-[#CCFF00]/40 bg-[#CCFF00]/[0.12] text-[#CCFF00] shadow-[0_0_24px_rgba(204,255,0,0.12)]"
      : "border border-transparent text-slate-500 hover:border-white/10 hover:bg-white/[0.04] hover:text-white",
  ].join(" ");
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(undefined);
  const token = getAdminToken();

  useEffect(() => {
    document.body.classList.add("pitch-admin-active");
    return () => document.body.classList.remove("pitch-admin-active");
  }, []);

  useEffect(() => {
    if (location.pathname === "/admin/login") return;
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setMe(undefined);
    let cancelled = false;
    (async () => {
      try {
        const data = await adminFetch("/v1/admin/me");
        if (!cancelled) setMe(data.user);
      } catch {
        setAdminToken(null);
        if (!cancelled) navigate("/admin/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate, token]);

  const logout = () => {
    setAdminToken(null);
    setMe(undefined);
    navigate("/admin/login");
  };

  if (location.pathname === "/admin/login") {
    return <Outlet />;
  }

  if (token && me === undefined) {
    return (
      <div className="pitch-admin-bg flex min-h-screen items-center justify-center text-slate-500">
        <div className="pitch-admin-panel flex flex-col items-center gap-4 px-10 py-12">
          <div className="h-10 w-10 animate-pulse rounded-lg border border-[#CCFF00]/30 bg-[#CCFF00]/10" />
          <p className="font-mono-data text-xs uppercase tracking-[0.25em] text-slate-400">
            Signing you in…
          </p>
        </div>
      </div>
    );
  }

  const isAdmin = me?.role === "admin";

  return (
    <div className="pitch-admin-bg min-h-screen text-white">
      <div className="pitch-admin-topline" />

      <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#050505]/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <Link
              to="/"
              className="group flex shrink-0 items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-2 py-2 transition-colors hover:border-[#CCFF00]/35"
              title="Back to marketing site"
            >
              <span className="flex h-10 w-10 items-center justify-center border border-[#CCFF00]/50 bg-[#CCFF00]/[0.08] text-sm font-black text-[#CCFF00] transition-transform group-hover:scale-[1.02]">
                P
              </span>
              <div className="min-w-0 leading-tight">
                <span className="block truncate text-[11px] font-black uppercase tracking-[0.28em] text-white">
                  Pitch console
                </span>
                <span className="font-mono-data block truncate text-[9px] uppercase tracking-[0.2em] text-slate-500">
                  News dashboard
                </span>
              </div>
            </Link>
          </div>

          <nav className="flex flex-wrap items-center gap-1.5 sm:justify-end">
            <NavLink to="/admin/feed" className={navClass}>
              Feed
            </NavLink>
            {isAdmin && (
              <>
                <NavLink to="/admin/customers" className={navClass}>
                  Customers
                </NavLink>
                <NavLink to="/admin/users" className={navClass}>
                  Team
                </NavLink>
                <NavLink to="/admin/audit" className={navClass}>
                  Audit
                </NavLink>
              </>
            )}
            <NavLink to="/admin/publish" className={navClass}>
              Publish
            </NavLink>
          </nav>

          <div className="flex items-center gap-3 border-t border-white/5 pt-3 sm:border-0 sm:pt-0">
            {me?.email && (
              <div className="hidden min-w-0 max-w-[200px] text-right sm:block">
                <span className="block truncate font-mono-data text-[11px] text-slate-300">
                  {me.email}
                </span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-[#CCFF00]/80">
                  {me.role}
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={logout}
              className="pitch-admin-btn-ghost whitespace-nowrap"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <Outlet context={{ me }} />
      </main>
    </div>
  );
}